import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { TESTIDS } from "@/constants/testIds";
import { t } from "@/lib/i18n";
import { NavLangSwitch } from "@/lib/learnLang";

const linkCls = "text-sm text-[#1C201F] hover:text-[#2F524A] transition-colors px-3 py-2";
const mobileLinkCls = "block text-base text-[#1C201F] hover:bg-[#F4F7F6] rounded-lg px-3 py-3 transition-colors";

const navLinks = (user) => {
  if (!user) {
    return [
      { to: "/assistance", label: t("Assistance"), testid: "nav-assistance-link-guest" },
      { to: "/map", label: t("Community"), testid: "nav-map-link-guest" },
      { to: "/learn", label: t("Learn"), testid: "nav-learn-link-guest" },
      { to: "/login", label: t("Log in"), testid: TESTIDS.navLogin },
    ];
  }
  const links = [
    { to: "/dashboard", label: t("Dashboard"), testid: TESTIDS.navDashboard },
    { to: "/assistance", label: t("Assistance"), testid: "nav-assistance-link" },
    { to: "/map", label: t("Community"), testid: "nav-map-link" },
    { to: "/learn", label: t("Learn"), testid: "nav-learn-link" },
  ];
  if (user.role === "admin") links.push({ to: "/admin", label: t("Admin"), testid: "nav-admin-link", strong: true });
  return links;
};

const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const authed = user && typeof user === "object";
  const links = navLinks(authed ? user : null);

  useEffect(() => setOpen(false), [location.pathname]);

  const doLogout = () => {
    logout();
    navigate("/");
  };

  const signup = (testid) => (
    <Link to="/signup" data-testid={testid} className="btn-primary !py-2 !px-4 sm:!px-5 text-sm whitespace-nowrap shrink-0">
      {t("Get started")}
    </Link>
  );

  const action = authed ? (
    <button data-testid={TESTIDS.navLogout} onClick={doLogout} className="btn-secondary !py-2 !px-4 text-sm">
      {t("Log out")}
    </button>
  ) : signup(TESTIDS.navSignup);

  return (
    <header className="w-full border-b border-[#E8ECEA] bg-white/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid={TESTIDS.navHome} className="flex items-center gap-2 min-w-0">
          <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: "#2F524A" }} />
          <span className="font-serif text-base sm:text-xl text-[#1C201F] whitespace-nowrap truncate">
            {t("Radon Action & Tracking")}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-4" data-testid="nav-desktop">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={l.testid}
              className={l.strong ? "text-sm text-[#2F524A] font-semibold hover:underline px-3 py-2" : linkCls}>
              {l.label}
            </Link>
          ))}
          <NavLangSwitch />
          {authed && <span className="text-sm text-[#5C6661]">{user.email}</span>}
          {action}
        </nav>

        <div className="flex lg:hidden items-center gap-2">
          {!authed && <span className="hidden sm:inline-flex">{signup("nav-signup-link-mobile")}</span>}
          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? t("Close menu") : t("Open menu")}
            aria-expanded={open}
            data-testid="nav-mobile-toggle"
            className="p-2 rounded-full border border-[#E8ECEA] text-[#1C201F] hover:bg-[#F4F7F6] transition-colors"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E8ECEA] bg-white shadow-sm" data-testid="nav-mobile-menu">
          <nav className="max-w-7xl mx-auto px-5 py-3 space-y-1">
            <NavLangSwitch testid="mobile-nav-lang-switch" className="block" />
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`mobile-${l.testid}`}
                className={`${mobileLinkCls} ${l.strong ? "text-[#2F524A] font-semibold" : ""}`}>
                {l.label}
              </Link>
            ))}
            {!authed && (
              <Link to="/signup" data-testid="mobile-nav-signup-link"
                className="sm:hidden btn-primary !py-3 text-sm w-full justify-center mt-2 inline-flex">
                {t("Get started")}
              </Link>
            )}
            {authed && (
              <div className="border-t border-[#E8ECEA] mt-2 pt-3 flex items-center justify-between gap-3 px-3">
                <span className="text-sm text-[#5C6661] truncate" data-testid="nav-mobile-email">{user.email}</span>
                <button data-testid="mobile-nav-logout-button" onClick={doLogout}
                  className="btn-secondary !py-2 !px-4 text-sm shrink-0">
                  {t("Log out")}
                </button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default NavBar;
